import { useState, useCallback } from 'react';
import { runWsjfTool } from '../../tools/wsjf';
import { ItemInput } from './components/ItemInput';
import { ScoringView } from './components/ScoringView';
import { ResultsView } from './components/ResultsView';
import type { WSJFItem } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { LiveSequentialScore } from '../../components/prioritization/session/liveExperience/liveTools/LiveSequentialScore';
import { LiveToolComplete } from '../../components/prioritization/session/liveExperience/liveTools/LiveToolComplete';

type AppState = 'input' | 'scoring' | 'results';

const WSJF_DEFINITIONS: Record<'costOfDelay' | 'jobDuration', string> = {
  costOfDelay: 'The business cost incurred by delaying this item — including value lost, risk increase, and opportunity cost. Higher means delaying is more expensive.',
  jobDuration: 'The relative size or duration of this job. Higher means it takes more time or resources to complete.',
};

function WSJFContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, shouldAutoStart, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  
  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<WSJFItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scoringPhase, setScoringPhase] = useState<'costOfDelay' | 'jobDuration'>('costOfDelay');
  // Live: the participant pressed Finish on the last item and sees their ranking.
  const [liveDone, setLiveDone] = useState(false);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: WSJFItem[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      costOfDelay: null,
      jobDuration: null,
      wsjf: null,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    setItems(prev => [...prev, ...newItems]);

    if (shouldAutoStart) {
      setCurrentItemIndex(0);
      setScoringPhase('costOfDelay');
      setState('scoring');
    }
  }, [shouldAutoStart]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by WSJF score (highest first)
    const sortedItems = [...items]
      .filter(item => item.wsjf !== null)
      .sort((a, b) => (b.wsjf || 0) - (a.wsjf || 0));
    
    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: idx + 1,
      score: item.wsjf || undefined,
      scoreBreakdown: {
        costOfDelay: item.costOfDelay || 0,
        jobDuration: item.jobDuration || 0,
      },
      metadata: {
        costOfDelay: item.costOfDelay,
        jobDuration: item.jobDuration,
      }
    }));
  }, [items, sourceIdMap]);

  const handleStart = (itemNames: string[]) => {
    const newItems: WSJFItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      costOfDelay: null,
      jobDuration: null,
      wsjf: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setScoringPhase('costOfDelay');
    setState('scoring');
  };
  
  const handleScoreUpdate = (field: 'costOfDelay' | 'jobDuration', value: number | null) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      [field]: value,
      // Worked out again at Finish, so a changed score is never submitted stale.
      wsjf: null,
    };
    setItems(updatedItems);
  };

  const handleNext = async () => {
    if (scoringPhase === 'costOfDelay') {
      setScoringPhase('jobDuration');
    } else if (scoringPhase === 'jobDuration') {
      // Move to next item or finish
      if (currentItemIndex < items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
        setScoringPhase('costOfDelay');
      } else {
        // Score via the canonical engine adapter (src/tools/wsjf.ts) — the
        // page collects inputs; the engine owns the WSJF math.
        const scoreSet = await runWsjfTool(
          items.map((item) => ({
            id: item.id,
            label: item.name,
            attributes: {
              cost_of_delay: item.costOfDelay,
              job_duration: item.jobDuration,
            },
          })),
          null,
          {}
        );
        const scoreById = new Map<string, number>(
          scoreSet.items.map((s) => [s.item_id as string, s.score as number])
        );
        setItems(items.map((item) => ({ ...item, wsjf: scoreById.get(item.id) ?? null })));
        if (isLiveSession) setLiveDone(true);
        else setState('results');
      }
    }
  };

  const handleBack = () => {
    if (scoringPhase === 'jobDuration') {
      setScoringPhase('costOfDelay');
    } else if (scoringPhase === 'costOfDelay' && currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setScoringPhase('jobDuration');
    }
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setScoringPhase('costOfDelay');
    setSourceIdMap({});
    setLiveDone(false);
  };

  const canProceed = () => {
    const current = items[currentItemIndex];
    if (!current) return false;
    if (scoringPhase === 'costOfDelay') {
      return current.costOfDelay !== null && current.costOfDelay > 0;
    }
    if (scoringPhase === 'jobDuration') {
      return current.jobDuration !== null && current.jobDuration > 0;
    }
    return false;
  };

  const currentItem = items[currentItemIndex];
  const canSubmitLive = isLiveSession && items.length > 0 && items.every(i => i.wsjf != null);
  const phaseValue =
    currentItem &&
    (scoringPhase === 'costOfDelay' ? currentItem.costOfDelay : currentItem.jobDuration);

  return (
    <ToolWrapper
      toolName="WSJF"
      toolDescription="Prioritize by dividing Cost of Delay by Job Duration"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={items.length}
      {...props}
    >
      <div className="w-full">
        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              {isAppMode && items.length === 0 && (
                <div className="mb-6 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                  <p className="text-sm text-[var(--text-sec)]">
                    {`Import items from your ${importSourceLabel} using the button in the header to begin WSJF scoring.`}
                  </p>
                </div>
              )}
              {/* Standalone mode - show ItemInput */}
              {!isAppMode && <ItemInput onStart={handleStart} />}
            </>
          )}

          {state === 'scoring' && isLiveSession && liveDone && (
            <LiveToolScaffold
              title="WSJF"
              subtitle="Your ranking is ready to submit"
              progressDone={items.length * 2}
              progressTotal={items.length * 2}
            >
              <LiveToolComplete
                heading="All items scored"
                items={handleGetResults().map((r, i) => ({
                  id: r.id,
                  name: r.name,
                  rank: r.rank ?? i + 1,
                  detail: `WSJF ${(r.score ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
                }))}
                onChangeScores={() => setLiveDone(false)}
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && isLiveSession && !liveDone && (
            <LiveToolScaffold
              title="WSJF"
              subtitle="Cost of delay ÷ job duration for each item"
              progressDone={currentItemIndex * 2 + (scoringPhase === 'jobDuration' ? 1 : 0)}
              progressTotal={items.length * 2}
            >
              <LiveSequentialScore
                itemName={currentItem.name}
                itemIndex={currentItemIndex}
                totalItems={items.length}
                phaseTitle={scoringPhase === 'costOfDelay' ? 'Cost of delay' : 'Job duration'}
                valueDefinition={WSJF_DEFINITIONS[scoringPhase]}
                phaseDescription={
                  scoringPhase === 'costOfDelay'
                    ? 'Relative cost of delay (higher = more urgent)'
                    : 'Relative job size / duration (higher = larger)'
                }
                placeholder="e.g. 8"
                inputValue={phaseValue?.toString() ?? ''}
                onInputChange={v => {
                  const num = v.trim() === '' ? null : parseFloat(v);
                  handleScoreUpdate(scoringPhase, num != null && !isNaN(num) ? num : null);
                }}
                onNext={handleNext}
                onBack={canProceed() ? handleBack : undefined}
                canProceed={canProceed()}
                isLastStep={
                  scoringPhase === 'jobDuration' && currentItemIndex === items.length - 1
                }
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && !isLiveSession && (
            <ScoringView
              item={currentItem}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              phase={scoringPhase}
              onScoreUpdate={handleScoreUpdate}
              onNext={handleNext}
              onBack={handleBack}
              canProceed={canProceed()}
              progress={((currentItemIndex * 2 + (scoringPhase === 'costOfDelay' ? 0 : 1)) / (items.length * 2)) * 100}
            />
          )}

          {state === 'results' && (
            <ResultsView 
              items={items} 
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function WeightedSmallestJobFirst(props: ToolHostProps) {
  return <WSJFContent {...props} />;
}
